import React, { useState } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { AppProvider, useAppContext } from './context/AppContext';
import { StockDataProvider } from './context/StockDataContext';
import { NotificationProvider } from './context/NotificationContext';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import StockList from './components/StockList';
import Watchlist from './components/Watchlist';
import Holdings from './components/Holdings';
import TransactionHistory from './components/TransactionHistory';
import ManualTrading from './components/ManualTrading';
import AccountManagement from './components/AccountManagement';
import UserManagement from './components/UserManagement';
import AnnualTransactionStats from './components/AnnualTransactionStats';
// AccountSelector is used in Header component
import AccessDenied from './components/AccessDenied';
import PendingApproval from './components/PendingApproval';
import BottomNavigation from './components/BottomNavigation';
import DesktopNavigation from './components/DesktopNavigation';
import LoginPage from './components/LoginPage';
import ErrorBoundary from './components/ErrorBoundary';

/**
 * 全域分頁索引定義
 * Header / BottomNavigation / DesktopNavigation / App 共用,
 * 取代散落各處的 magic number(FIN-08)
 */
export const TABS = {
  DASHBOARD: 0,
  STOCK_LIST: 1,
  WATCHLIST: 2,
  HOLDINGS: 3,
  TRANSACTION_HISTORY: 4,
  MANUAL_TRADING: 5,
  ANNUAL_STATS: 6,
  ACCOUNT_MANAGEMENT: 7,
  USER_MANAGEMENT: 8,
} as const;

// Create a modern, accessible theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1565c0',
      light: '#42a5f5',
      dark: '#0d47a1',
    },
    secondary: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#2c3e50',
      secondary: '#5a6c7d',
    },
  },
  typography: {
    fontFamily: '"Noto Sans TC", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontSize: '2rem',
      fontWeight: 700,
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1rem',
          padding: '10px 20px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
        },
      },
    },
  },
});

const AppContent: React.FC = () => {
  const { state } = useAppContext();
  const [currentTab, setCurrentTab] = useState<number>(TABS.HOLDINGS);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Listen for admin tab change events from Header
  React.useEffect(() => {
    const handleAdminTabChange = (event: CustomEvent) => {
      setCurrentTab(event.detail);
    };

    window.addEventListener('adminTabChange', handleAdminTabChange as EventListener);

    return () => {
      window.removeEventListener('adminTabChange', handleAdminTabChange as EventListener);
    };
  }, []);

  // Check if user is admin
  const isAdmin = state.googleUser?.role === 'admin';

  const renderTabContent = () => {
    switch (currentTab) {
      case TABS.DASHBOARD:
        return <Dashboard />;
      case TABS.STOCK_LIST:
        return <StockList />;
      case TABS.WATCHLIST:
        return <Watchlist />;
      case TABS.HOLDINGS:
        return <Holdings />;
      case TABS.TRANSACTION_HISTORY:
        return <TransactionHistory />;
      case TABS.MANUAL_TRADING:
        return <ManualTrading />;
      case TABS.ANNUAL_STATS:
        return <AnnualTransactionStats />;
      case TABS.ACCOUNT_MANAGEMENT:
        return isAdmin ? <AccountManagement /> : <Dashboard />;
      case TABS.USER_MANAGEMENT:
        return isAdmin ? <UserManagement /> : <Dashboard />;
      default:
        return <Dashboard />;
    }
  };

  // Show loading spinner during authentication check
  if (state.authLoading) {
    return (
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0,0,0,0.8)',
        }}
        open={true}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="primary" size={60} />
          <Box sx={{ fontSize: '18px', fontWeight: 'bold' }}>正在驗證身份...</Box>
        </Box>
      </Backdrop>
    );
  }

  // Show login page if user is not authenticated
  if (!state.authUser || !state.currentUser) {
    return <LoginPage />;
  }

  // Show pending approval page if user is not approved yet
  if (state.accessDenied && state.googleUser && !state.googleUser.isAllowed) {
    return <PendingApproval />;
  }
  
  // Show access denied screen if user doesn't have access to any accounts
  if (state.accessDenied) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Header />
        <AccessDenied />
      </Box>
    );
  }

  // Show loading spinner for app data
  if (state.isLoading) {
    return (
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0,0,0,0.8)',
        }}
        open={true}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="primary" size={60} />
          <Box sx={{ fontSize: '18px', fontWeight: 'bold' }}>載入資料中...</Box>
        </Box>
      </Backdrop>
    );
  }

  // Main authenticated app
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <DesktopNavigation currentTab={currentTab} onTabChange={setCurrentTab} />
      
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          pb: isMobile ? 10 : 2,
          px: { xs: 0, sm: 0 }, // Remove horizontal padding on mobile
          backgroundColor: '#f8f9fa',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        {renderTabContent()}
      </Box>
      
      <BottomNavigation currentTab={currentTab} onTabChange={setCurrentTab} />
    </Box>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <NotificationProvider>
          <StockDataProvider cacheValidityMinutes={30}>
            <AppProvider>
              <AppContent />
            </AppProvider>
          </StockDataProvider>
        </NotificationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
